import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authService } from '../../services/authService';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Use resetPassword which now generates fresh token for each request
      const { error } = await authService?.resetPassword(email);
      
      if (error) {
        setError(error?.message);
      } else {
        setSuccess(true);
      }
    } catch (err) {
      setError(err?.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-card rounded-lg shadow-lg p-8 border border-border">
            <div className="flex items-center justify-center mb-6">
              <div className="bg-success/10 rounded-full p-4">
                <Icon name="CheckCircle" size={48} className="text-success" />
              </div>
            </div>
            
            <h1 className="text-2xl font-bold text-center text-foreground mb-2">
              Check Your Email
            </h1>
            <p className="text-center text-muted-foreground mb-6">
              We've sent a password reset link to <span className="font-semibold text-foreground">{email}</span>
            </p>

            <div className="bg-muted/50 rounded-md p-4 mb-6">
              <p className="text-sm text-muted-foreground">
                <Icon name="Info" size={16} className="inline mr-1" />
                Didn't receive the email? Check your spam folder or try again in a few minutes.
              </p>
            </div>

            <Link to="/signin">
              <Button fullWidth variant="outline" iconName="ArrowLeft">
                Back to Sign In
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-card rounded-lg shadow-lg p-8 border border-border">
          <div className="flex items-center justify-center mb-6">
            <div className="bg-primary rounded-full p-3">
              <Icon name="Lock" size={32} className="text-white" />
            </div>
          </div>
          
          <h1 className="text-2xl font-bold text-center text-foreground mb-2">
            Forgot Password?
          </h1>
          <p className="text-center text-muted-foreground mb-6">
            Enter your email address and we'll send you a link to reset your password
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              type="email"
              label="Email Address"
              value={email}
              onChange={(e) => setEmail(e?.target?.value)}
              placeholder="Enter your email"
              required
              error={error}
            />

            {error && (
              <div className="bg-error/10 border border-error rounded-md p-3 flex items-start gap-2">
                <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
                <p className="text-sm text-error">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              fullWidth
              loading={loading}
              iconName="Mail"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/signin" className="text-sm text-muted-foreground hover:text-foreground flex items-center justify-center gap-1">
              <Icon name="ArrowLeft" size={16} />
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;